import { Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { ApiService } from "./api.service";
import { apiColletions } from "src/app/core/constants/api_collection";
import { Helper } from "src/app/core/helpers/helper";

declare let stripe: any;

@Injectable({
  providedIn: "root"
})
export class PaymentService {
  constructor(private apiService: ApiService, private helper: Helper) {}

  public updateCards = new BehaviorSubject<any>(null);
  updateCardsObservable = this.updateCards.asObservable();

  public paymentCompleted = new BehaviorSubject<boolean>(false);
  paymentCompletedObservable = this.paymentCompleted.asObservable();

  getPaymentGetway(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.getPaymentGateway, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getAddCardPaymentIntent(type: any, parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService
        .post({
          url: `${apiColletions.addCardPaymentIntent}/${type}`,
          parameters
        })
        .subscribe({
          next: (response) => {
            if (response.data) {
              observer.next(response.data.data);
              observer.complete();
            } else {
              observer.next(false);
              observer.complete();
            }
          },
          error: () => {
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  addNewCard(type: any, parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService
        .post({
          url: `${apiColletions.cardApi}/${type}`,
          parameters
        })
        .subscribe({
          next: (response) => {
            if (response.data) {
              observer.next(response.data.data);
              observer.complete();
            } else {
              observer.next(false);
              observer.complete();
            }
          },
          error: () => {
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  getCardList(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService
        .get({
          url: apiColletions.cardApi,
          parameters
        })
        .subscribe({
          next: (response) => {
            if (response.data) {
              observer.next(response.data.data);
              observer.complete();
            } else {
              observer.next(false);
              observer.complete();
            }
          },
          error: () => {
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  selectCard(cardId: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const api = `${apiColletions.cardApi}/${cardId}`;
      this.apiService.patch({ url: api, parameters: {} }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
            this.updateCards.next({});
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  deleteCard(cardId: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const api = `${apiColletions.cardApi}/${cardId}`;
      this.apiService.delete({ url: api, parameters: {} }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
            this.updateCards.next({});
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  createPaymentIntent(type: any, parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const api = `${apiColletions.addWalletPaymentIntent}/${type}`;
      this.apiService.post({ url: api, parameters: parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  transactionHistory(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.transactionHistory, parameters: parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getPendingPaymentIntents(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.paymentIntent, parameters: parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  amountPayment(paymentMethod: number, paymentParams: any): Observable<any> {
    const params: any = {
      amount: Number(paymentParams.amount),
      currency: paymentParams.currency,
      countryId: paymentParams.countryId,
      paymentPurpose: paymentParams.paymentPurpose
    };
    if (paymentParams.bookingId) {
      params.bookingId = paymentParams.bookingId;
    }

    return this.createPaymentIntent(paymentMethod, params).pipe(
      switchMap((res: any) => this.amountCreatedPayment(res)) // Separate function for handling payment gateways
    );
  }

  amountCreatedPayment(res: any): Observable<boolean> {
    this.apiService.apiLoadingSubject.next(true);
    if (res.paymentGatewayType === this.helper.PaymentGatewayEnum.Stripe && res.paymentTransactionStatus === this.helper.PaymentTransactionStatus.INITIATED) {
      return from(this.handleStripePayment(res)); // Convert promise to observable
    } else if (this.helper.PaymentGatewayHandleUrlEnumArray.includes(res.paymentGatewayType) && res.paymentTransactionStatus === this.helper.PaymentTransactionStatus.INITIATED) {
      return from(this.handlePaystackPayment(res)); // Convert promise to observable
    } else if (this.helper.PaymentGatewayHandleHtmlEnumArray.includes(res.paymentGatewayType)) {
      return from(this.handleRazorpayPayment(res)); // Convert promise to observable
    } else if (res.paymentTransactionStatus === this.helper.PaymentTransactionStatus.PAID) {
      this.paymentCompleted.next(true);
      this.apiService.apiLoadingSubject.next(false);
      return of(true); // If already paid, return true
    }
    this.apiService.apiLoadingSubject.next(false);
    return of(false); // If none of the conditions match, return false
  }

  private handleStripePayment(res: any): Promise<boolean> {
    return new Promise((resolve) => {
      stripe
        .confirmCardPayment(res.intent.clientSecret, {
          // eslint-disable-next-line camelcase
          payment_method: res.intent.paymentMethod
        })
        .then((result: any) => {
          if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
            this.helper.toasterService.showNotification("success", this.helper.translateService.instant("label-title.payment-created-successfully"));
            this.paymentCompleted.next(true);
            resolve(true); // Payment succeeded
          } else if (result.paymentIntent && result.paymentIntent.status === "requires_capture") {
            resolve(true);
          } else {
            resolve(false); // Payment failed but no error thrown
          }
          this.apiService.apiLoadingSubject.next(false);
        });
    });
  }

  private handlePaystackPayment(res: any): Promise<boolean> {
    return new Promise((resolve) => {
      const paystackWindow = window.open(res.intent.url, "_blank", `width=${this.helper.width},height=${this.helper.height},left=${this.helper.left},top=${this.helper.top}`);
      if (paystackWindow) {
        if (!this.helper.messageListenerAdded) {
          window.addEventListener("message", this.helper.getMessage.bind(this), false);
          this.helper.messageListenerAdded = true; // Set flag after adding listener
        }

        const paystackWindowCheckInterval = setInterval(() => {
          if (paystackWindow.closed) {
            this.apiService.apiLoadingSubject.next(false);
            clearInterval(paystackWindowCheckInterval);
            this.paymentCompleted.next(false);
            resolve(false); // Closing the window is not proof the payment succeeded
          }
        }, 500);
      } else {
        this.apiService.apiLoadingSubject.next(false);
        resolve(false); // Resolve with false if the window couldn't be opened
      }
    });
  }

  private handleRazorpayPayment(res: any): Promise<boolean> {
    return new Promise((resolve) => {
      const newWindow = window.open("", "_blank", `width=${this.helper.width},height=${this.helper.height},left=${this.helper.left},top=${this.helper.top}`);
      if (newWindow) {
        // Define the message listener
        let listenerActive = false;
        const handlePaymentMessage: any = (event: any) => {
          // Only the payment window may report a result. Any other page can post { success: true }.
          if (event.source !== newWindow) return;
          if (event.data.success) {
            this.paymentCompleted.next(true);
            this.helper.toasterService.showNotification("success", event.data.message);
            listenerActive = true;
            this.apiService.apiLoadingSubject.next(false);
            resolve(true);
          } else if (event.data.message) {
            this.helper.toasterService.showNotification("error", event.data.message);
            listenerActive = false;
            this.apiService.apiLoadingSubject.next(false);
            resolve(false);
          }
          // Clean up listener after handling the message
          if (event.data.message) {
            window.removeEventListener("message", handlePaymentMessage);
            this.helper.messageListenerAdded = false;
          }
        };

        if (!this.helper.messageListenerAdded) {
          window.addEventListener("message", handlePaymentMessage, false);
          this.helper.messageListenerAdded = true; // Set flag after adding listener
        }
        // Write the Razorpay form HTML to the new window
        newWindow.document.write(res.intent.html);

        const modalCheckInterval = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(modalCheckInterval);
            this.apiService.apiLoadingSubject.next(false);
            // Resolve based on whether the listener was activated
            resolve(listenerActive);
          }
        }, 500);
      }
    });
  }

  addCard(selectedGatewayType: number, params: any): Observable<any> {
    return this.getAddCardPaymentIntent(selectedGatewayType, params).pipe(
      map((response: any) => {
        if (selectedGatewayType === this.helper.PaymentGatewayType[this.helper.PaymentGatewayEnum.Stripe]) {
          // Return Stripe response
          return { success: true, gatewaytype: this.helper.PaymentGatewayType[this.helper.PaymentGatewayEnum.Stripe], data: response };
        } else if (selectedGatewayType === this.helper.PaymentGatewayType[this.helper.PaymentGatewayEnum.Paystack]) {
          // Return Paystack response
          window.open(response.intent.authorization_url, "_blank", `width=${this.helper.width},height=${this.helper.height},left=${this.helper.left},top=${this.helper.top}`);
          // Event handler to receive messages from child window
          if (!this.helper.messageListenerAdded) {
            window.addEventListener("message", this.helper.getMessage, false);
            this.helper.messageListenerAdded = true; // Set flag after adding listener
          }
          return { success: true, gatewaytype: this.helper.PaymentGatewayType[this.helper.PaymentGatewayEnum.Paystack], data: response };
        }
        return { success: false }; // Return false if no matching gateway
      })
    );
  }
}
